import {
  collection, query, where, orderBy, getDocs,
  doc, getDoc, setDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function geminiUrl() {
  const key = process.env.REACT_APP_GEMINI_API_KEY;
return `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${key}`;
}

// Single batch read for the whole org — one Firestore call instead of one per product.
export async function fetchOrgSalesHistory(orgId) {
  const since = new Date();
  since.setDate(since.getDate() - 14);

  const q = query(
    collection(db, 'activityLog'),
    where('orgId', '==', orgId),
    where('timestamp', '>=', Timestamp.fromDate(since)),
    orderBy('timestamp', 'desc')
  );

  const snap = await getDocs(q);
  const byProduct = {};

  snap.docs.forEach(d => {
    const data = d.data();
    // Only sales and write-offs consume stock — restocks and returns are supply, not demand.
    if (!['sale', 'writeoff'].includes(data.transactionType)) return;
    if (!byProduct[data.productId]) byProduct[data.productId] = [];
    byProduct[data.productId].push({
      date: data.timestamp?.toDate().toISOString().split('T')[0],
      qty: Math.abs(data.delta || 0),
      type: data.transactionType,
    });
  });

  return byProduct;
}

async function getCachedForecast(orgId, productId, forceFresh) {
  if (forceFresh) return null;
  try {
    const snap = await getDoc(doc(db, 'forecasts', `${orgId}_${productId}`));
    if (!snap.exists()) return null;
    const data = snap.data();
    const ageMs = Date.now() - (data.cachedAt?.toMillis() || 0);
    if (ageMs > CACHE_TTL_MS) return null;
    return data.result;
  } catch {
    return null;
  }
}

async function setCachedForecast(orgId, productId, result) {
  try {
    await setDoc(doc(db, 'forecasts', `${orgId}_${productId}`), {
      result,
      cachedAt: Timestamp.now(),
      orgId,
      productId,
    });
  } catch (err) {
    // Cache write failure is non-fatal — the result still gets shown.
    console.warn('Could not cache forecast:', err);
  }
}

export async function getForecastForProduct(product, salesHistory, orgId, forceFresh = false) {
  const cached = await getCachedForecast(orgId, product.id, forceFresh);
  if (cached) return { ...cached, fromCache: true };

  const transactions = salesHistory[product.id] || [];

  if (transactions.length < 3) {
    return {
      avgDailyConsumption: 0,
      daysUntilStockout: null,
      recommendedOrderQty: null,
      confidence: 'low',
      reason: 'Not enough data yet — needs at least 3 sale events in 14 days.',
      fromCache: false,
    };
  }

  const minStockNote = product.minStock != null
    ? `${product.minStock} units`
    : `not set (MOQ is ${product.moq})`;

  const prompt = `You are an inventory analyst for a small Indian retail business. Analyze this product and predict stockout risk.

Product: "${product.name}" (group: ${product.group})
Current stock: ${product.stock} units
Minimum stock threshold: ${minStockNote}

Sales and write-offs in the last 14 days (newest first):
${JSON.stringify(transactions, null, 2)}

Analyze this data and respond ONLY with a valid JSON object — no markdown, no explanation, no extra text:
{"avgDailyConsumption":number,"daysUntilStockout":number,"recommendedOrderQty":number,"confidence":"high"|"medium"|"low","reason":"one concise sentence max 12 words"}

Rules:
- avgDailyConsumption: total qty consumed / 14 (use 0 if no data)
- daysUntilStockout: floor(currentStock / avgDailyConsumption), or 999 if consumption is 0
- recommendedOrderQty: quantity to order to cover 30 days of demand, rounded to nearest 5
- confidence: "high" if 7+ transactions and consistent daily rate, "medium" if 3-6 or variable, "low" if sparse
- reason: plain English, max 12 words`;

  try {
    const res = await fetch(geminiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        // 1. Bump maxOutputTokens much higher — thinking tokens don't count against output
//    but the response itself needs room:
generationConfig: { temperature: 0.1, maxOutputTokens: 1024 },

// 2. Disable thinking entirely — we don't need it for simple JSON extraction:
generationConfig: { 
  temperature: 0.1, 
  maxOutputTokens: 1024,
  thinkingConfig: { thinkingBudget: 0 }
},
      }),
    });

    if (!res.ok) throw new Error(`Gemini ${res.status}`);

    const data = await res.json();
  const raw = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

// strip markdown fences, grab only the first {...} block
const match = raw.match(/\{[\s\S]*\}/);
if (!match) throw new Error('No JSON object found in Gemini response');
const result = JSON.parse(match[0]);
if (!res.ok) {
  const body = await res.text();
  throw new Error(`Gemini ${res.status}: ${body}`);
}

    await setCachedForecast(orgId, product.id, result);
    return { ...result, fromCache: false };
  } catch (err) {
    console.error(`Forecast failed for ${product.name}:`, err);
    return {
      avgDailyConsumption: null,
      daysUntilStockout: null,
      recommendedOrderQty: null,
      confidence: 'low',
      reason: 'Forecast unavailable — will retry on next refresh.',
      fromCache: false,
    };
  }
}